import React from 'react';

const EDGE_CONFIGURATIONS = [
  ['1_short', '1 short edge'],
  ['1_long', '1 long edge'],
  ['2_short', '2 short edges'],
  ['2_long', '2 long edges'],
  ['4_edges', '4 edges'],
];

const FinishSelector = ({
  availableFinishes = [],
  selectedFinishes = [],
  onChange,
  allowMultiple = true,
  finishingMaterials = {}, // Object with finish_id as key, array of materials as value
  finishingOptions = {}, // Object with finish_id as key, options object as value
  onFinishingOptionsChange, // Callback for when finishing options change
}) => {
  const handleToggleFinish = (finishId) => {
    const postClipsId = 'post-clips';
    const signChannelId = 'adding-sign-channel';

    if (allowMultiple) {
      if (selectedFinishes.includes(finishId)) {
        const finishesToRemove = finishId === signChannelId
          ? new Set([signChannelId, postClipsId])
          : new Set([finishId]);
        onChange(selectedFinishes.filter((id) => !finishesToRemove.has(id)));
      } else {
        const nextFinishes = new Set(selectedFinishes);
        nextFinishes.add(finishId);
        if (finishId === postClipsId) {
          nextFinishes.add(signChannelId);
        }
        onChange(Array.from(nextFinishes));
      }
    } else {
      onChange(selectedFinishes.includes(finishId) ? [] : [finishId]);
    }
  };

  const handleFinishingOptionChange = (finishId, optionKey, value) => {
    if (onFinishingOptionsChange) {
      const updatedOptions = {
        ...finishingOptions,
        [finishId]: {
          ...(finishingOptions[finishId] || {}),
          [optionKey]: value,
        },
      };
      onFinishingOptionsChange(updatedOptions);
    }
  };

  if (!availableFinishes.length) {
    return <p className="text-sm text-gray-500">No finishes available for this job type.</p>;
  }

  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {availableFinishes.map((finish) => {
        const isSelected = selectedFinishes.includes(finish.id);
        const materials = finishingMaterials[finish.id] || [];
        const hasMaterials = materials.length > 0;
        const selectedMaterial = finishingOptions[finish.id]?.material || '';
        const edgeConfig =
          finishingOptions[finish.id]?.edge_config ||
          finishingOptions[finish.id]?.edge_count ||
          finishingOptions[finish.id]?.edges ||
          '2_short';
        const edgeConfigurations = finish.id === 'velcro'
          ? [
              ...EDGE_CONFIGURATIONS.slice(0, 4),
              ['2_short_1_long', '2 short + 1 long'],
              ['2_long_1_short', '2 long + 1 short'],
              EDGE_CONFIGURATIONS[4],
            ]
          : EDGE_CONFIGURATIONS;

        return (
          <div key={finish.id}>
            <label
              className={`flex cursor-pointer items-start gap-3 rounded border p-3 text-sm transition-colors ${
                isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <input
                type={allowMultiple ? 'checkbox' : 'radio'}
                checked={isSelected}
                onChange={() => handleToggleFinish(finish.id)}
                className="mt-1 h-4 w-4"
              />
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-gray-900">{finish.name}</div>
                  {isSelected && (
                    <div className="flex flex-wrap items-center gap-2">
                      {['velcro', 'double-sided-tape'].includes(finish.id) && (
                        <select
                          value={edgeConfig}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleFinishingOptionChange(
                              finish.id,
                              'edge_config',
                              e.target.value,
                            );
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded border border-gray-300 px-2 py-0.5 text-xs shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        >
                          {edgeConfigurations.map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                      )}
                      {hasMaterials && (
                        <select
                          value={selectedMaterial}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleFinishingOptionChange(finish.id, 'material', e.target.value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded border border-gray-300 px-2 py-0.5 text-xs shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Select type...</option>
                          {materials.map((mat) => (
                            <option key={mat.id} value={mat.material}>
                              {mat.material}
                            </option>
                          ))}
                        </select>
                      )}
                      {finish.id === 'drilling-and-standoffs' && (
                        <input
                          type="number"
                          min="0"
                          value={finishingOptions[finish.id]?.standoff_quantity ?? ''}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleFinishingOptionChange(
                              finish.id,
                              'standoff_quantity',
                              e.target.value,
                            );
                          }}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Per board"
                          className="w-22 rounded border border-gray-300 px-2 py-0.5 text-xs shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                      )}
                      {finish.id === 'post-clips' && (
                        <input
                          type="number"
                          min="0"
                          value={finishingOptions[finish.id]?.clip_quantity ?? 1}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleFinishingOptionChange(
                              finish.id,
                              'clip_quantity',
                              e.target.value,
                            );
                          }}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Per board"
                          className="w-22 rounded border border-gray-300 px-2 py-0.5 text-xs shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                      )}
                    </div>
                  )}
                </div>
                {finish.description && (
                  <p className="mt-1 text-xs text-gray-600">{finish.description}</p>
                )}
              </div>
            </label>
          </div>
        );
      })}
    </div>
  );
};

export default FinishSelector;
